import React from "react";
import { StudentTabs } from "./StudentTabs";
import { Route, Routes } from "react-router-dom";
import { routes } from "consts";
import { StudentApplicationForm } from "./StudentApplicationForm";
import { StudentPaymentsTab } from "./panels/StudentPaymentsTab";
import { StudentExamsTab } from "./panels/StudentExamsTab";
import { EmptyState } from "components/molecules";
import { HiOutlineExternalLink } from "react-icons/hi";
import { StudentActionsTab } from "./panels/StudentActionsTab";
import { useCourse } from "api/hooks";
import { useStudentCourses } from "api/student-courses";
import { formatDate } from "utils";
import { PaymentStatusBadge, paymentStateOf } from "./PaymentStatusBadge";

interface StudentPanelsProps {
  studentId: number;
  courseId: number;
  className?: string;
}

export const StudentPanels = ({ studentId, courseId, className = "" }: StudentPanelsProps) => {
  const { loading: loadingCourse, data: course } = useCourse(courseId, false);
  const { courses } = useStudentCourses(studentId);

  if (!courseId)
    return (
      <div
        className={`col-span-12 flex min-h-[400px] items-center justify-center rounded-xl border border-slate-200 bg-white p-8 md:col-span-8 dark:border-slate-700 dark:bg-slate-800 ${className}`}
      >
        <EmptyState
          title="Podgląd kursu"
          description="Wybierz kurs z listy, aby zobaczyć szczegóły"
        />
      </div>
    );

  const studentCourse = courses.find((c) => c.courseId === courseId);
  const paymentState = studentCourse ? paymentStateOf(studentCourse.fullyPaid, studentCourse.paidAmount) : undefined;
  const subtitle = [course?.data.edition, course?.data.variant].filter(Boolean).join(" · ");

  return (
    <div
      className={`col-span-12 overflow-hidden rounded-xl border border-slate-200 bg-white md:col-span-8 dark:border-slate-700 dark:bg-slate-800 ${className}`}
    >
      <div className="flex max-h-[calc(100vh-12rem)] min-h-[400px] flex-col overflow-hidden">
        <div className="relative flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-700/50">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
                {loadingCourse ? "Ładowanie…" : course?.data.name || "Szczegóły kursu"}
              </h3>
              {paymentState && <PaymentStatusBadge state={paymentState} />}
            </div>
            {(subtitle || studentCourse) && (
              <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                {subtitle}
                {studentCourse && `${subtitle ? " · " : ""}zapisano ${formatDate(studentCourse.registrationDate, false, false)}`}
              </p>
            )}
          </div>
          <a
            href={routes.COURSES_MANAGE(courseId)}
            target="_blank"
            rel="noreferrer"
            className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            <HiOutlineExternalLink size={14} />
            Otwórz kurs
          </a>
        </div>
        <StudentTabs studentId={studentId} courseId={courseId} />
        <div className="scrollbar-thin scrollbar-thumb-slate-300 dark:scrollbar-thumb-slate-600 scrollbar-track-transparent flex-1 overflow-y-auto p-4">
          <Routes>
            <Route path={""} element={<StudentApplicationForm studentId={studentId} courseId={Number(courseId)} />} />
            <Route
              path={routes.STUDENT_ACTIONS}
              element={<StudentActionsTab userId={studentId} courseId={Number(courseId)} />}
            />
            <Route path={routes.STUDENT_PAYMENTS} element={<StudentPaymentsTab />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};
